<template>
  <DataTable
      scrollable
      scroll-height="flex"
      :value="assetsRecentlyViewedPageStore.getAssets"
      lazy
      paginator
      :first="assetsRecentlyViewedPageStore.getFirstPage"
      :rows="assetsRecentlyViewedPageStore.params.per_page"
      :rowsPerPageOptions="assetsRecentlyViewedPageStore.pages"
      :total-records="assetsRecentlyViewedPageStore.totalRecords"
      :loading="assetsRecentlyViewedPageStore.loading"
      @page="assetsRecentlyViewedPageStore.onPage($event)"
      @update:rows="assetsRecentlyViewedPageStore.onRowsUpdate"
      data-key="id"
      size="small"
      always-show-paginator
  >
    <template #header>
      <Toolbar class="border-none shadow-none -m-3.5 rounded-none">
        <template #start>
          <Button data-cy="asset-create-btn" v-if="$auth.hasPermission('create:assets')" :label="$t('common.create')" class="mr-2"
                  @click="createAsset">
            <template #icon="slotProps">
              <IconPlus class="w-4 h-4 mr-1" :class="slotProps.class"/>
            </template>
          </Button>
        </template>

        <template #end>
          <div class="flex items-stretch justify-end space-x-2">
            <div class="flex-1 divide-x divide-slate-300 grid grid-cols-3">
              <IndexHeaderStat
                  title="Assets Total"
                  :value="assetsDashboardPageStore?.getAssetsTotalCount"
                  :loading="assetsDashboardPageStore?.getAssetsTotalCountLoading"
              />
              <IndexHeaderStat
                  title="Without Devices"
                  :value="assetsDashboardPageStore?.getAssetsWithoutDevicesCount"
                  :loading="assetsDashboardPageStore?.getAssetsWithoutDevicesCountLoading"
              />
              <IndexHeaderStat
                  title="Needing An Update"
                  :value="assetsDashboardPageStore?.getAssetsNeedingAnUpdateCount"
                  :loading="assetsDashboardPageStore?.getAssetsNeedingAnUpdateCountLoading"
              />
            </div>
            <Button text severity="secondary" @click="refreshStats(true)">
              <template #icon="{class: iconClass}">
                <IconRefresh :class="iconClass" class="w-5 h-5"/>
              </template>
            </Button>
          </div>
        </template>
      </Toolbar>
    </template>
    <Column field="id" :header="$t('common.id')" class="pl-6 lg:pl-8 w-20 uppercase"/>
    <Column field="name" :header="$t('common.name')" class="w-64 whitespace-nowrap">
      <template #body="{data}">
        <div class="flex items-center justify-start space-x-3">
          <div class="h-9 w-9 bg-primary-50 rounded-md inline-flex items-center justify-center">
            <IconTruck class="text-primary-900 opacity-50"/>
          </div>
          <router-link :to="{name: 'assets.show', params: { asset: data.id }}">
            <Button
                :label="data.name"
                link
                size="small"
                aria-haspopup="true"
            />
          </router-link>
        </div>
      </template>
    </Column>
    <Column field="last_viewed_at" :header="$t('common.last_viewed')" class="w-44 whitespace-nowrap">
      <template #body="{data}">
        {{ formatDate(data.last_viewed_at) }}
      </template>
    </Column>
    <Column field="asset_type.name" :header="$t('common.type')" class="w-32 whitespace-nowrap">
      <template #body="{data}">
        {{ data.asset_type?.name || '-' }}
      </template>
    </Column>
    <Column field="company.name" :header="$t('common.company')" class="w-56 whitespace-nowrap">
      <template #body="{data}">
        {{ data.company?.name || '-' }}
      </template>
    </Column>
    <Column field="devices" :header="$t('common.devices')">
      <template #body="{data}">
        <div class="flex flex-row space-x-1.5">
          <Tag :severity="device.requires_update === 1 ? 'info' : 'secondary'" v-for="device in data?.devices"
               :value="device?.device_model?.name"
               class="whitespace-nowrap"
          >
            <template #icon="{ class: iconClass }">
              <IconAlertTriangleFilled v-if="device.requires_update === 1" :class="iconClass" class="h-4 w-4"/>
            </template>
          </Tag>
        </div>
      </template>
    </Column>
    <Column field="devices" :header="$t('pages.assets.index.rx_swins')">
      <template #body="{data}">
        <div class="flex flex-row space-x-1.5">
          <Tag :severity="'secondary'" v-for="rxSwin in data?.rx_swins"
               class="whitespace-nowrap"
          >
            <span>{{ rxSwin.regulation.name }}_{{ rxSwin.identifier }}</span>
          </Tag>
        </div>
      </template>
    </Column>
  </DataTable>

  <AssetSlideOver ref="slideOver" @saved="onSave"/>
</template>

<script>
import {useAssetsRecentlyViewedPageStore} from "@/stores/pages/assetsRecentlyViewedPage.js";
import {useAssetsDashboardPageStore} from "@/stores/pages/assetsDashboardPage.js";
import {mapStores} from "pinia";
import AssetSlideOver from "@/Slideovers/AssetSlideOver.vue";
import {IconAlertTriangleFilled, IconPlus, IconTruck, IconRefresh} from "@tabler/icons-vue";
import IndexHeaderStat from "@/Widgets/Cards/IndexHeaderStat.vue";
import dayjs from "dayjs";

export default {
  name: "AssetsRecentlyViewedView",
  computed: {
    ...mapStores(useAssetsRecentlyViewedPageStore),
    ...mapStores(useAssetsDashboardPageStore),
  },
  components: {
    IndexHeaderStat,
    IconTruck,
    IconPlus,
    IconAlertTriangleFilled,
    AssetSlideOver,
    IconRefresh,
  },
  methods: {
    editAsset(asset) {
      this.$refs.slideOver.editAsset(asset);
    },
    createAsset() {
      this.$refs.slideOver.createAsset();
    },
    onSave() {
      this.assetsRecentlyViewedPageStore.loadData();
      this.refreshStats(true);
    },
    refreshStats(forceRefresh = false) {
      this.assetsDashboardPageStore.fetchAssetCount({}, forceRefresh);
      this.assetsDashboardPageStore.fetchAssetsWithoutDevicesCount({}, forceRefresh);
      this.assetsDashboardPageStore.fetchAssetsNeedingAnUpdate({}, forceRefresh);
    },
    formatDate(date) {
      return date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-';
    }
  },
  async mounted() {
    try {
      await this.assetsRecentlyViewedPageStore.loadData();
    } catch (error) {
      console.error('Failed to load recently viewed assets:', error);
    } finally {
      this.refreshStats();
    }
  }
}
</script>
